import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Check, X, ImagePlus, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/api";

const inputCls = "rounded-lg border border-clayline bg-cream px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-terracotta/60";
const emptyForm = { category: "", name: "", price: "", s: "", m: "", l: "", description: "", image: "" };
const fileUrl = (p) => (p ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}` : "");

const MenuManager = () => {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () =>
    api.get("/menu").then((r) => setGroups(r.data)).catch(() => toast.error("Failed to load menu"));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const move = async (gi, ii, dir) => {
    const group = groups[gi];
    const j = ii + dir;
    if (j < 0 || j >= group.items.length) return;
    const next = groups.map((g, idx) =>
      idx === gi ? { ...g, items: [...g.items] } : g
    );
    [next[gi].items[ii], next[gi].items[j]] = [next[gi].items[j], next[gi].items[ii]];
    setGroups(next);
    try {
      await api.post("/admin/menu/reorder", { ids: next.flatMap((g) => g.items.map((i) => i.id)) });
      toast.success("Order saved — live on the menu");
    } catch {
      toast.error("Could not save order");
      load();
    }
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setAdding(false);
    setForm({
      category: item.category,
      name: item.name,
      price: item.price != null ? String(item.price) : "",
      s: item.sizes?.S ?? "",
      m: item.sizes?.M ?? "",
      l: item.sizes?.L ?? "",
      description: item.description || "",
      image: item.image || "",
    });
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const buildPayload = () => ({
    category: form.category.trim(),
    name: form.name.trim(),
    price: form.price === "" ? null : parseFloat(form.price),
    sizes: form.s !== "" && form.m !== "" && form.l !== "" ? { S: +form.s, M: +form.m, L: +form.l } : null,
    description: form.description.trim(),
    image: form.image,
  });

  const save = async () => {
    const p = buildPayload();
    if (!p.category || !p.name) {
      toast.error("Name and category are required");
      return;
    }
    if (p.price == null && !p.sizes) {
      toast.error("Set a price or S/M/L sizes");
      return;
    }
    try {
      if (adding) await api.post("/admin/menu", p);
      else await api.put(`/admin/menu/${editing}`, p);
      toast.success(adding ? "Item added to the menu" : "Item updated");
      setEditing(null);
      setAdding(false);
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Save failed. Please try again.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the menu?`)) return;
    try {
      await api.delete(`/admin/menu/${item.id}`);
      toast.success("Item deleted");
      load();
    } catch {
      toast.error("Delete failed. Please try again.");
    }
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setForm(emptyForm);
  };

  const editForm = (
    <div className="bg-sand/60 border border-terracotta/50 rounded-xl p-4 grid sm:grid-cols-2 gap-3" data-testid="menu-edit-form">
      <input placeholder="Item name" value={form.name} onChange={set("name")} className={inputCls} data-testid="menu-form-name" />
      <input placeholder="Category" value={form.category} onChange={set("category")} className={inputCls} data-testid="menu-form-category" list="menu-categories" />
      <datalist id="menu-categories">
        {groups.map((g) => <option key={g.category} value={g.category} />)}
      </datalist>
      <input placeholder="Single price ₹ (leave blank if sizes)" value={form.price} onChange={set("price")} type="number" min="0" className={inputCls} data-testid="menu-form-price" />
      <div className="flex gap-2">
        <input placeholder="S ₹" value={form.s} onChange={set("s")} type="number" min="0" className={inputCls} data-testid="menu-form-s" />
        <input placeholder="M ₹" value={form.m} onChange={set("m")} type="number" min="0" className={inputCls} data-testid="menu-form-m" />
        <input placeholder="L ₹" value={form.l} onChange={set("l")} type="number" min="0" className={inputCls} data-testid="menu-form-l" />
      </div>
      <input placeholder="Description (optional)" value={form.description} onChange={set("description")} className={`${inputCls} sm:col-span-2`} data-testid="menu-form-description" />
      <div className="sm:col-span-2 flex items-center gap-4">
        {form.image ? (
          <div className="flex items-center gap-3">
            <img src={fileUrl(form.image)} alt="Item" className="w-16 h-16 rounded-xl object-cover border border-clayline" data-testid="menu-form-image-preview" />
            <button type="button" onClick={() => setForm({ ...form, image: "" })} data-testid="menu-form-image-remove" className="font-mono text-[11px] uppercase tracking-wider text-red-600 hover:underline">
              Remove
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-dashed border-terracotta text-terracotta cursor-pointer hover:bg-terracotta hover:text-cream transition-colors" data-testid="menu-form-image-upload">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploading ? "Uploading…" : "Add Photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} />
          </label>
        )}
      </div>
      <div className="sm:col-span-2 flex gap-3">
        <button onClick={save} data-testid="menu-save-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center gap-2">
          <Check size={14} /> Save
        </button>
        <button onClick={cancel} data-testid="menu-cancel-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-clayline hover:border-charcoal transition-colors inline-flex items-center gap-2">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="menu-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">
          {groups.reduce((s, g) => s + g.items.length, 0)} items · {groups.length} categories · arrows set menu order
        </p>
        <button
          onClick={() => { setAdding(true); setEditing(null); setForm(emptyForm); }}
          data-testid="menu-add-btn"
          className="rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-charcoal text-cream hover:bg-terracotta transition-colors inline-flex items-center gap-2"
        >
          <Plus size={14} /> Add Item
        </button>
      </div>
      {adding && <div className="mb-6">{editForm}</div>}
      <div className="space-y-8">
        {groups.map((g, gi) => (
          <div key={g.category} className="bg-cream border border-clayline rounded-2xl p-6" data-testid={`menu-group-${g.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <h3 className="font-serif font-bold text-2xl italic mb-4">{g.category} <span className="font-mono text-xs not-italic text-charcoal/40 tracking-[0.2em]">({g.items.length})</span></h3>
            <div className="divide-y divide-clayline/60">
              {g.items.map((item, ii) => (
                editing === item.id ? (
                  <div key={item.id} className="py-4">{editForm}</div>
                ) : (
                  <div key={item.id} className="py-3 flex items-center gap-3 group" data-testid={`menu-item-row-${item.id.slice(0, 8)}`}>
                    <div className="flex flex-col shrink-0 -my-1">
                      <button
                        onClick={() => move(gi, ii, -1)}
                        disabled={ii === 0}
                        data-testid={`menu-up-${item.id.slice(0, 8)}`}
                        className="p-1 text-charcoal/50 hover:text-terracotta disabled:opacity-20 transition-colors"
                        aria-label={`Move ${item.name} up`}
                      >
                        <ChevronUp size={15} />
                      </button>
                      <button
                        onClick={() => move(gi, ii, 1)}
                        disabled={ii === g.items.length - 1}
                        data-testid={`menu-down-${item.id.slice(0, 8)}`}
                        className="p-1 text-charcoal/50 hover:text-terracotta disabled:opacity-20 transition-colors"
                        aria-label={`Move ${item.name} down`}
                      >
                        <ChevronDown size={15} />
                      </button>
                    </div>
                    {item.image ? (
                      <img src={fileUrl(item.image)} alt={item.name} className="w-11 h-11 rounded-lg object-cover border border-clayline shrink-0" />
                    ) : (
                      <span className="w-11 h-11 rounded-lg border border-dashed border-clayline shrink-0 flex items-center justify-center text-charcoal/25"><ImagePlus size={15} /></span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{item.name}</p>
                      {item.description && <p className="text-xs text-charcoal/50 truncate">{item.description}</p>}
                    </div>
                    <span className="font-mono text-sm text-terracotta whitespace-nowrap">
                      {item.sizes ? `S ₹${item.sizes.S} · M ₹${item.sizes.M} · L ₹${item.sizes.L}` : item.price != null ? `₹${item.price}` : "—"}
                    </span>
                    <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(item)} data-testid={`menu-edit-${item.id.slice(0, 8)}`} className="p-2 hover:text-terracotta transition-colors" aria-label={`Edit ${item.name}`}>
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => remove(item)} data-testid={`menu-delete-${item.id.slice(0, 8)}`} className="p-2 hover:text-red-600 transition-colors" aria-label={`Delete ${item.name}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MenuManager;
